import { guards } from '@shared/helpers/typeguards.helpers';

/** Pointer anchor for the project-list map hover tooltip. */
export type ProjectMapTooltipPoint = {
  x: number;
  y: number;
  projectNames?: string[];
};

/** Feature fields the map hover tooltip reads. Other properties are ignored. */
export type ProjectMapHoverFeature = {
  geometry?: { type?: string } | null;
  properties?: {
    projectId?: unknown;
    projectName?: unknown;
    cluster_id?: unknown;
    point_count?: unknown;
  } | null;
};

/**
 * What the project list map should show for the feature under the pointer.
 * `tooltip` without `projectNames` is the selected-outline "Open project" title.
 */
export type ProjectMapHoverTooltip =
  | { kind: 'cluster'; clusterId: number; count: number }
  | { kind: 'tooltip'; point: ProjectMapTooltipPoint }
  | { kind: 'hidden' };

/**
 * Chooses the project-list map tooltip for the feature under the pointer.
 *
 * Clusters defer their names to Mapbox leaves. A single pin shows its project
 * name unless that project is selected: clicking a selected pin clears the
 * selection, so the pin must not reuse the outline title "Open project".
 * Building outlines keep their previous tooltip, including that title when no
 * name is shown.
 */
export const resolveProjectMapHoverTooltip = (
  feature: ProjectMapHoverFeature | undefined,
  anchor: { x: number; y: number },
  selectedProjectId: string | undefined,
): ProjectMapHoverTooltip => {
  const clusterId = feature?.properties?.cluster_id;
  const count = feature?.properties?.point_count;
  if (guards.is.number(clusterId) && guards.is.number(count))
    return { kind: 'cluster', clusterId, count };

  const geometryType = feature?.geometry?.type;
  const isPin = geometryType === 'Point';
  const isOutline =
    geometryType === 'Polygon' || geometryType === 'MultiPolygon';
  if (!feature || (!isPin && !isOutline)) return { kind: 'hidden' };

  const projectName = feature.properties?.projectName;
  const showsName =
    feature.properties?.projectId !== selectedProjectId &&
    guards.is.string(projectName);
  // A pin with no visible name must not fall through to "Open project".
  if (isPin && !showsName) return { kind: 'hidden' };

  return {
    kind: 'tooltip',
    point: {
      ...anchor,
      projectNames: showsName ? [projectName] : undefined,
    },
  };
};

/**
 * Project names to list in a cluster tooltip, one string per row.
 * Non-string leaf names are dropped.
 */
export const clusterProjectNames = (
  leaves: ReadonlyArray<{
    properties?: { projectName?: unknown } | null;
  }>,
) =>
  leaves.flatMap((leaf) => {
    const projectName = leaf.properties?.projectName;
    if (!guards.is.string(projectName)) return [];
    return [projectName];
  });
